import * as THREE from 'three';
import {
  AMBIENT_COLOR, AMBIENT_INTENSITY, BACKGROUND_COLOR, BRAZIER_COLOR, BRAZIER_INTENSITY, BRAZIER_RANGE_M,
  BRAZIER_LIGHT_SLOTS, LIGHT_SLOT_FADE_S, LANTERN_COLOR, LANTERN_INTENSITY, LANTERN_RANGE_M, LANTERN_HEIGHT_M,
  LANTERN_LIGHT_SLOTS, MAX_PIXEL_RATIO, MIN_PIXEL_RATIO,
  DIRECTIONAL_COLOR, DIRECTIONAL_INTENSITY, DIRECTIONAL_POSITION,
  FILL_COLOR, FILL_INTENSITY, FILL_POSITION,
} from '../config/lighting-tuning';
import { MONSTER_ROSTER } from '../config/monster-roster';
import { assignLightSlots, LightSourcePos } from './light-pool';
import { flamePosition } from './torch-flame';
import { isLitAt } from './monster-reveal';
import { AdaptiveResolution } from './adaptive-resolution';
import type { ModelInstance } from './instanced-layout';
import { CharacterVisual } from './character-visual';
import { rosterEntry, CharacterClassId } from '../config/character-roster';
import { monsterRosterEntry, MonsterArchetype } from '../config/monster-roster';
import * as adventurersCache from './adventurers-cache';
import * as skeletonsCache from './skeletons-cache';
import { computeShoulderCameraPose } from './shoulder-camera-pose';
import { PostProcessingStack } from './post-processing';
import { stepFovKick } from './fov-kick';

/**
 * design/gdd/camera-and-controls.md's Camera and control table. FOV and boom
 * bumped from the spec's original 55/70/2.2 (2026-08-21 amendment) after live
 * play showed the character filling too much of the frame — the GDD's own
 * Tuning Knobs table already flagged this exact failure mode ("Too Low: hurts
 * peripheral awareness" / "Too narrow: claustrophobic"), and corridors are
 * where the FOV heuristic and boom collision both bite hardest at once.
 * Widening FOV rather than pulling the boom back further was the primary
 * lever, since a longer boom risks losing "close enough for melee reach
 * reading" (source spec §2) — a wider FOV directly shrinks apparent character
 * size and adds peripheral view without moving the eye away from the action.
 */
/**
 * Combat-readability pass (user report: the character's own body fills so much
 * of the frame you can't see your own swing — and this is the SECOND time
 * "the character fills too much of the frame" has come back, see this file's
 * header comment).
 *
 * Quantified rather than eyeballed this time. Visible frame height at
 * distance d is `2 * d * tan(fov / 2)`, and the KayKit adventurer stands
 * ~1.75m, so the old numbers put the character at:
 *   boom 2.5m @ 68 deg  -> 3.37m visible -> 52% of frame height
 *   boom 1.8m @ 68 deg  -> 2.43m visible -> 72%  (the collision minimum)
 * Third-person action games generally sit the player at 25-35%. Tallow was
 * running roughly 1.5-2x oversized, which is exactly why a swing behind your
 * own torso is invisible.
 *
 * The header comment records that last time the fix was to widen FOV instead
 * of lengthening the boom, to protect "close enough for melee reach reading"
 * (source spec 2). That lever has now been spent — FOV is already 68-85 — and
 * the problem came back, so this pass moves the boom. Melee reach is 1.6-2.8m,
 * and reading it arguably gets EASIER with the whole reach envelope in frame
 * rather than cropped by your own shoulder. New framing:
 *   boom 3.8m @ 68 deg -> 5.14m visible -> 34%
 *   boom 3.8m @ 85 deg -> 6.96m visible -> 25%
 * and the collision floor at 2.6m keeps the worst case near 50% instead of 72%.
 *
 * The lateral offset grows too: at 0.45m the body sat close to frame centre,
 * directly over where the weapon travels. Pushing it out clears the swing arc.
 */
const SHOULDER_OFFSET_M = 0.7;
const SHOULDER_HEIGHT_M = 1.6;
const BOOM_LENGTH_M = 3.8;
const FOV_CORRIDOR_DEG = 68;
const FOV_OPEN_ROOM_DEG = 85;
const FOV_BLEND_TAU_S = 0.4 / 3; // spec's stated 0.4s blend, ~3 time-constants to settle
/** Below this average opening distance reads as "corridor"; above it reads as "large room." */
const OPENNESS_CORRIDOR_M = 2.5;
const OPENNESS_OPEN_ROOM_M = 8;
const OPENNESS_PROBE_MAX_M = 10;
/** 8-point horizontal ring, sampled from the player's own position (not the camera) to gauge how enclosed the space is. */
const OPENNESS_PROBE_ANGLES = Array.from({ length: 8 }, (_, i) => (i / 8) * Math.PI * 2);

/** Kept clear of a wall hit so the near clip plane never pokes through it. */
const CAMERA_COLLISION_MARGIN = 0.3;
/**
 * **Lowered from 2.0 (2026-08-25). This is the fix for "the camera gets
 * blocked by textures and similar objects."**
 *
 * This value is a floor that OVERRIDES the wall hit (`max(MIN, hit - margin)`),
 * so every metre of it is a metre the camera is allowed to sit *inside*
 * geometry. The previous comment here acknowledged exactly that trade-off and
 * accepted it, on the grounds that the alternative put the eye inside the
 * character's own head. Instrumented over a 2.2s walk, the cost of that
 * choice was: 55 of 245 frames had geometry nearer than the floor, and at the
 * worst frame a wall 1.30m away was overridden to hold the boom at 2.00m,
 * placing the eye 0.70m through the wall. Being inside a wall IS the reported
 * bug — you are looking at its interior.
 *
 * So geometry wins now, and the head problem is solved where it belongs: the
 * local player's own model fades out as the eye closes on it
 * (`localPlayerFadeAlpha` in render/proximity-fade.ts). A near-first-person
 * shot in a tight corner still shows the room; the inside of a wall does not.
 *
 * 0.5m rather than 0: the margin still has to fit, and the eye should not pass
 * the character's own centre line.
 */
const MIN_CAMERA_DISTANCE = 0.5;
/**
 * dungeon-instance.md follow-up (2026-08-21) — boomLength itself was applied
 * raw, un-smoothed, straight from this frame's raycast result, unlike every
 * other camera parameter here (FOV, shoulder blend). Found live via a
 * temporary position-history dump: the eye jumped ~0.7m in a single 8ms
 * frame with the player's own position essentially unchanged — the raycast
 * ring flickering between "clear" and "grazing a wall corner" from one tick
 * to the next as facing shifted by a fraction of a degree, with nothing
 * smoothing the transition. A short exponential blend (matching the
 * shoulder-swap's own tau) removes the single-frame snap without making
 * genuine wall pull-in feel sluggish.
 */
const BOOM_BLEND_TAU_S = 0.067;
/**
 * Game Feel Polish Phase 4 — a punchy widen on sprint/dash, composed
 * additively on top of the openness-based FOV blend above (not a second
 * competing target for the same field) so the two never fight: the
 * openness blend keeps tracking corridor-vs-room as normal, this just adds
 * a few degrees on top while sprinting/dodging and releases when it stops.
 * Attack is fast (snappy onset), release is slower (settles instead of
 * snapping back) — the standard asymmetric shape for this kind of kick.
 */
const FOV_SPRINT_KICK_DEG = 6;
const FOV_KICK_ATTACK_TAU_S = 0.1;
const FOV_KICK_RELEASE_TAU_S = 0.3;
/** design/gdd/skeleton-ai.md monsters — a cool rim reads clearly for silhouette separation against this scene's warm torch/lantern light without a real dynamic light per monster (cheaper, and works at any monster count). See CharacterVisual.applyRimLight. */
/** Every playable class, warmed by `warmUp` — see there. */
const WARM_CLASSES: readonly CharacterClassId[] = ['rogue', 'knight', 'barbarian', 'druid'];
const WARM_GEOMETRY = new THREE.PlaneGeometry(0.01, 0.01);

/**
 * One material per MeshBasicMaterial program the run's short-lived effects
 * use — flags copied from `character-visual.ts` (impact flash, level-up
 * column and ring), `target-ring.ts` and `ability-fx.ts`. A program is keyed
 * on these flags, not on colour or opacity, so one of each is enough.
 */
function warmUpEffectMaterials(): THREE.MeshBasicMaterial[] {
  const additive = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending } as const;
  return [
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 }),
    new THREE.MeshBasicMaterial({ ...additive, opacity: 0.5, side: THREE.BackSide }),
    new THREE.MeshBasicMaterial({ ...additive, opacity: 0.5, side: THREE.DoubleSide }),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }),
  ];
}

const MONSTER_RIM_LIGHT_COLOR = 0x9fc9ff;
const MONSTER_RIM_LIGHT_POWER = 2.5;
/**
 * Raised from 0.5 alongside the post-Phase-4 brightness fix. The rim term is
 * additive (`gl_FragColor.rgb += rimColor * rimFactor * rimIntensity`), so
 * its absolute contribution didn't change when ambient roughly doubled — but
 * the base it sits on top of did, which drops its relative contrast and
 * weakens exactly the silhouette separation it exists for. Compensated
 * partially (1.4x against a ~2x brighter base) rather than fully, to lift the
 * cue back without turning it into an outline. Reasoned from how the shader
 * composites, not observed against a monster in the same pass — worth an
 * eyeball next time one is on screen.
 */
const MONSTER_RIM_LIGHT_INTENSITY = 0.7;
/**
 * A single ray from the shoulder anchor to the eye only catches walls dead
 * center of that line — a wall grazing the side of the camera (a corner, a
 * narrow corridor) sits outside that one ray but still fills the frame at
 * this FOV, which is what "the camera rotation and camera itself collide
 * with textures" (a wall filling a large part of the screen) actually was.
 * Approximated with a small ring of parallel probe rays around the center
 * one instead of a single ray, cheaply standing in for a real cone/cylinder
 * cast.
 */
const COLLISION_PROBE_RADIUS_M = 0.3;
const COLLISION_PROBE_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [COLLISION_PROBE_RADIUS_M, 0], [-COLLISION_PROBE_RADIUS_M, 0],
  [0, COLLISION_PROBE_RADIUS_M], [0, -COLLISION_PROBE_RADIUS_M],
];

function expApproach(current: number, target: number, dtSeconds: number, tauSeconds: number): number {
  return current + (target - current) * (1 - Math.exp(-dtSeconds / tauSeconds));
}

const BEACON_COLORS: Record<string, number> = {
  UNBARRED: 0x33cc55,
  BARRED: 0xdd3333,
  SEALED: 0xdd3333,
  CRANKING: 0xddaa22,
  OPEN: 0x33cc55,
  CLOSED: 0xd4af37,
  CHEST_OPEN: 0x777777,
};

/**
 * Renders design/gdd/dungeon-instance.md's live match: real generated
 * geometry (design/gdd/procedural-layout-assembly.md, via
 * buildInstancedLayout) plus players and world-state objects. World-state
 * objects are simple color-coded beacons rather than oriented door/gate
 * models — same "primitive placeholder, not final art" precedent as
 * scene.ts's player capsules (game-concept.md character/prop packs aren't
 * sourced yet); a beacon needs no wall-orientation data to be legible.
 */
/**
 * design/gdd/equipment.md — what a delver is holding, straight off the synced
 * `PlayerState.equippedWeapon`/`equippedOffhand`.
 *
 * Empty `weaponModel` means the state predates the paperdoll (or a bot), and
 * the class's roster arms are used exactly as before.
 */
export interface PlayerArms {
  weaponModel?: string;
  offhandModel?: string;
  /** equipment.md's worn-gear amendment — `PlayerState.wornGear`, already split. */
  wornGear?: readonly string[];
}

export class DungeonInstanceScene {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  private readonly doorBeacons = new Map<string, THREE.Mesh>();
  private readonly portcullisBeacons = new Map<string, THREE.Mesh>();
  private readonly chestBeacons = new Map<string, THREE.Mesh>();
  /**
   * Braziers as data, and a fixed pool of real lights handed to the nearest
   * lit ones (2026-10-15, QA #9 — see `light-pool.ts` for why one PointLight
   * per torch was the GPU's biggest bill and the source of shader-recompile
   * hitches). `pos` is the flame, not the mount (QA #15a, `torch-flame.ts`).
   */
  private readonly braziers = new Map<string, { x: number; z: number; pos: THREE.Vector3; lit: boolean }>();
  private readonly brazierSlots: Array<{ light: THREE.PointLight; id: string | null; level: number }> = [];
  /** Characters carrying a lit lantern, by session id — the lantern pool follows their roots. */
  private readonly lanternHolders = new Map<string, THREE.Object3D>();
  private readonly lanternSlots: Array<{ light: THREE.PointLight; id: string | null }> = [];
  private lightAnchors: readonly ModelInstance[] = [];
  private lastSlotAssignAt = -Infinity;
  private lastLightStepAt = performance.now();
  private readonly adaptiveResolution: AdaptiveResolution;
  private layoutGroup: THREE.Group | null = null;
  private readonly resizeHandler = () => this.onResize();
  // Reused every frame in followLocalPlayer's collision raycast — CLAUDE.md's
  // no-per-frame-allocations rule for render loops.
  private readonly raycaster = new THREE.Raycaster();
  private readonly shoulderAnchor = new THREE.Vector3();
  private readonly forwardVec = new THREE.Vector3();
  private readonly rightVec = new THREE.Vector3();
  private readonly probeOrigin = new THREE.Vector3();
  private readonly probeDirection = new THREE.Vector3();
  /** Smoothed toward the openness-derived target each frame — see followLocalPlayer(). */
  private currentFovDeg = FOV_OPEN_ROOM_DEG;
  /** Additive sprint/dash kick on top of currentFovDeg — see followLocalPlayer(). */
  private currentFovKickDeg = 0;
  /** Read once at construction — motion prefs don't change mid-session in practice, and the kick is the only motion effect in this class opting into the check (camera shake/shoulder-swap predate this convention). */
  private readonly reducedMotion = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  private readonly postProcessing: PostProcessingStack;
  /** Smoothed toward the collision-raycast target each frame — see followLocalPlayer(). */
  private currentBoomLength = BOOM_LENGTH_M;
  /**
   * How far the eye currently sits from the shoulder anchor. This is the
   * honest measure of "how close is the camera to my own body", which is why
   * the self-fade reads it rather than measuring to the model: a
   * CharacterVisual's `root` is at the FEET, so with the eye at
   * SHOULDER_HEIGHT_M a fully collapsed 0.5m boom still measures ~1.68m to
   * the root — far enough to look uncollapsed. Found live: the body stayed
   * fully opaque and filled 40% of the frame at a 0.5m boom.
   */
  get cameraBoomLengthM(): number { return this.currentBoomLength; }
  /**
   * Combat-feel amendment (2026-08-21) — user report: "combat still feels
   * stiff and wooden." A landed hit had zero feedback beyond a screen tint
   * on the defender's own client and a text log line — nothing on the
   * attacker's side, ever, and nothing any bystander could see. This is a
   * decaying random positional jitter applied on top of the normal camera
   * pose, triggered from `combat.result` for a hit the local player either
   * landed or took (dungeon-instance-bootstrap.ts).
   */
  private shakeAmplitudeM = 0;
  private shakeDurationMs = 0;
  private shakeElapsedMs = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.scene.background = new THREE.Color(BACKGROUND_COLOR);

    this.camera = new THREE.PerspectiveCamera(this.currentFovDeg, window.innerWidth / window.innerHeight, 0.1, 300);
    this.camera.position.set(0, 12, -12);
    this.camera.lookAt(0, 0, 0);

    // `antialias` off (2026-10-15, QA #9): everything is drawn into the post
    // stack's own render targets, which have no MSAA, so the multisampled
    // default framebuffer only ever received the final full-screen quad — a
    // resolve per frame that antialiased nothing.
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    const maxRatio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);
    this.renderer.setPixelRatio(maxRatio);
    this.postProcessing = new PostProcessingStack(this.renderer, this.scene, this.camera);
    this.adaptiveResolution = new AdaptiveResolution(maxRatio, Math.min(maxRatio, MIN_PIXEL_RATIO));

    this.buildLighting();
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Post-Phase-4 playability fix (user report: "it's really too dark, I can't
   * see anything" — the character silhouette and floor were barely readable).
   * Phase 4 stacked ACES tone mapping, SSAO and a vignette on top of a scene
   * that was already dark by design, and the compounding was tuned against
   * screenshots judged "moody" rather than against actually playing.
   *
   * Raised deliberately rather than cautiously, because design/gdd/lighting.md
   * Core Rule #4 is explicit that light has NO observer-side effect: "a player
   * holding a lit lantern doesn't see any further into a dark room than one
   * without... the only gameplay effect of light is on how far away *others*
   * can see you." Darkness is tactical information about your own visibility,
   * never an impairment of what you can see — so a dim render isn't enforcing
   * the mechanic, it's just fighting the player. The brazier/lantern
   * illumination still reads clearly on top of this floor level.
   */
  private buildLighting(): void {
    // All values live in config/lighting-tuning.ts, which carries the
    // measurements behind them and the reason the previous pass failed.
    this.scene.add(new THREE.AmbientLight(AMBIENT_COLOR, AMBIENT_INTENSITY));

    const key = new THREE.DirectionalLight(DIRECTIONAL_COLOR, DIRECTIONAL_INTENSITY);
    key.position.set(...DIRECTIONAL_POSITION);
    this.scene.add(key);

    // The opposing fill. A corridor has two facing walls and one key light can
    // only ever favour one of them; without this the other is lit by ambient
    // alone and reads as a black box.
    const fill = new THREE.DirectionalLight(FILL_COLOR, FILL_INTENSITY);
    fill.position.set(...FILL_POSITION);
    this.scene.add(fill);
  }

  /**
   * Constant light count for the life of the scene — see `braziers` above.
   * Created dark; `stepLights` hands them out. Lazy (2026-10-16): the Arena
   * reuses this scene and has no braziers or lanterns, and twelve dark lights
   * would still be twelve lights every arena fragment loops over. Both pools
   * come together on the first brazier, so a lantern lit later does not
   * change the light count either.
   */
  private ensureLightPools(): void {
    if (this.brazierSlots.length > 0) return;
    for (let i = 0; i < BRAZIER_LIGHT_SLOTS; i++) {
      const light = new THREE.PointLight(BRAZIER_COLOR, 0, BRAZIER_RANGE_M);
      this.scene.add(light);
      this.brazierSlots.push({ light, id: null, level: 0 });
    }
    for (let i = 0; i < LANTERN_LIGHT_SLOTS; i++) {
      const light = new THREE.PointLight(LANTERN_COLOR, 0, LANTERN_RANGE_M);
      this.scene.add(light);
      this.lanternSlots.push({ light, id: null });
    }
  }

  /** `instances` is the same list the group was built from — the torch models in it say where each brazier's flame is. */
  setLayoutGroup(group: THREE.Group, instances: readonly ModelInstance[] = []): void {
    if (this.layoutGroup) this.scene.remove(this.layoutGroup);
    this.layoutGroup = group;
    this.scene.add(group);
    this.lightAnchors = instances;
    // Braziers can arrive in state before the layout finishes loading.
    for (const b of this.braziers.values()) {
      const f = flamePosition(b.x, b.z, instances);
      b.pos.set(f.x, f.y, f.z);
    }
  }

  /**
   * Compiles every shader the run will need while the entry veil is still up
   * (2026-10-15, QA #9 "hitches"). Monster models load the first time one
   * comes into view, and each archetype's first appearance compiled its
   * skinned, rim-lit materials on that frame — a measured 0.5s+ freeze in the
   * middle of a corridor. One of each is loaded, compiled and dropped here.
   * Never throws: a warm-up that fails just means the old first-sight cost.
   */
  async warmUp(): Promise<void> {
    const warm: CharacterVisual[] = [];
    const staging = new THREE.Group();
    try {
      for (const entry of MONSTER_ROSTER) {
        warm.push(await this.addMonster(entry.id));
      }
      /**
       * The first-walk hitch (QA 10.10.2026 #9): 239-357ms on the first few
       * steps, renderer.info.programs 35 -> 39, all four MeshBasicMaterial.
       * Those are the camera-proximity fade's depth-only twins
       * (`CharacterVisual.setCameraFade`) — skinned and static, created the
       * first time the boom shortens against a wall and the body goes
       * translucent — plus the translucent variants of the body materials
       * themselves. So one delver of every class is loaded too, and every
       * warm body is put mid-fade: twins visible, materials transparent.
       */
      for (const classId of WARM_CLASSES) warm.push(await this.addPlayer(classId));
      for (const visual of warm) {
        visual.root.position.set(this.camera.position.x, -50, this.camera.position.z);
        visual.setCameraFade(0.9);
      }
      // The short-lived basic-material effects (hit flash, level-up column
      // and ring, target ring, ability plates) — same flags, same fog.
      for (const material of warmUpEffectMaterials()) {
        const mesh = new THREE.Mesh(WARM_GEOMETRY, material);
        mesh.frustumCulled = false;
        staging.add(mesh);
      }
      staging.position.set(this.camera.position.x, -50, this.camera.position.z);
      this.scene.add(staging);
      await this.renderer.compileAsync(this.scene, this.camera);
    } catch {
      // Best effort only.
    } finally {
      // Removed, not disposed: the geometry is the shared cache's, and
      // disposing it would only force a re-upload on the first real monster.
      // The staging materials are not disposed either — disposing the last
      // user of a program releases it, which would undo the warm-up.
      for (const visual of warm) visual.root.removeFromParent();
      staging.removeFromParent();
    }
  }

  /**
   * design/gdd/character-visuals.md Core Rule #10 — no local/remote
   * distinction here; the camera already makes "which one is me" obvious.
   * Async because it loads/clones a skinned model + retargets animation
   * (adventurers-cache.ts + character-visual.ts) — the caller (bootstrap)
   * owns tracking the in-flight promise per session.
   */
  async addPlayer(classId: CharacterClassId, skinAssetKey?: string, arms?: PlayerArms): Promise<CharacterVisual> {
    // design/gdd/equipment.md — the arms the delver chose on the paperdoll,
    // synced as `PlayerState.equippedWeapon`/`equippedOffhand`. An equipment
    // def's id *is* its model filename (asserted in the server's own
    // `equipment.test.ts`), so the state string is usable directly here and
    // there is no client-side catalog to keep in sync.
    const entry = { ...rosterEntry(classId), skinAssetKey };
    // An absent `weaponModel` means the state predates the paperdoll (or is a
    // bot), and the class's roster arms are used exactly as before. Once it is
    // set the whole choice is authoritative, including "no off-hand" — so the
    // shield is dropped rather than left at the class default.
    if (arms?.weaponModel) {
      entry.weaponModel = arms.weaponModel;
      if (arms.offhandModel) entry.shieldModel = arms.offhandModel;
      else delete entry.shieldModel;
    }
    // Independent of `weaponModel`: a delver in a full Ward set holding the
    // class's own free sword sends no `equippedWeapon` and must still be drawn
    // wearing the set.
    const withGear = { ...entry, wornGear: arms?.wornGear };
    const visual = await CharacterVisual.create(withGear, adventurersCache);
    this.scene.add(visual.root);
    return visual;
  }

  /**
   * design/gdd/skeleton-ai.md's Bone Warden amendment (2026-08-20) —
   * monsters render through the exact same `CharacterVisual` pipeline as
   * players (identical `EntityStateName` state machine, per `MonsterState`
   * mirroring `PlayerState`), just resolved against the monster roster and
   * the skeletons asset pack instead.
   */
  async addMonster(archetype: MonsterArchetype): Promise<CharacterVisual> {
    const visual = await CharacterVisual.create(monsterRosterEntry(archetype), skeletonsCache);
    // Game Feel Polish Phase 4 — monsters only (not players): the practical
    // need is spotting distant enemies, not the local/remote player models.
    visual.applyRimLight(MONSTER_RIM_LIGHT_COLOR, MONSTER_RIM_LIGHT_POWER, MONSTER_RIM_LIGHT_INTENSITY);
    this.scene.add(visual.root);
    return visual;
  }

  /**
   * design/gdd/camera-and-controls.md — fixed over-the-shoulder rig.
   * `viewYaw`/`viewPitch`/`shoulderSide` come from OverShoulderCameraController.
   * Boom collision: a small ring of parallel probe rays (not just one center
   * ray) from the shoulder anchor toward the eye, against the real level
   * geometry — a wall grazing the side of the view (a corner, a narrow
   * corridor) still pulls the camera in, not just a wall dead-center of a
   * single ray. FOV interpolates between the spec's corridor (55°) and open-
   * room (70°) values based on how much open floor surrounds the player,
   * sampled independently of the camera's own facing.
   */
  /**
   * A weaker shake already in progress never overrides a stronger one still
   * decaying — otherwise a light "you landed a hit" shake arriving right
   * after a heavy "you got hit" shake would visibly cut the stronger one
   * short instead of just adding to the moment.
   */
  shakeCamera(amplitudeM: number, durationMs: number): void {
    if (amplitudeM < this.shakeAmplitudeM) return;
    this.shakeAmplitudeM = amplitudeM;
    this.shakeDurationMs = durationMs;
    this.shakeElapsedMs = 0;
  }

  /** game-concept.md §13a — the run-start fly-through drives the camera directly, bypassing followLocalPlayer's shoulder-rig math entirely. */
  setFreeCameraPose(eye: { x: number; y: number; z: number }, lookAt: { x: number; y: number; z: number }): void {
    this.camera.position.set(eye.x, eye.y, eye.z);
    this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
  }

  followLocalPlayer(
    pos: { x: number; z: number }, viewYaw: number, viewPitch: number, shoulderSide: number, dtSeconds: number,
    sprintOrDashActive = false,
  ): void {
    this.forwardVec.set(Math.sin(viewYaw) * Math.cos(viewPitch), Math.sin(viewPitch), Math.cos(viewYaw) * Math.cos(viewPitch));
    // From yaw alone, not (-forward.z, 0, forward.x) — see the same correction
    // in shoulder-camera-pose.ts: that cross product is only unit-length at
    // pitch 0, so both the shoulder anchor and the probe-ring offsets below
    // used to shrink toward the centre line as the view pitched.
    this.rightVec.set(-Math.cos(viewYaw), 0, Math.sin(viewYaw));
    this.shoulderAnchor.set(
      pos.x + this.rightVec.x * SHOULDER_OFFSET_M * shoulderSide,
      SHOULDER_HEIGHT_M,
      pos.z + this.rightVec.z * SHOULDER_OFFSET_M * shoulderSide,
    );

    let targetBoomLength = BOOM_LENGTH_M;
    if (this.layoutGroup) {
      this.raycaster.near = 0.05;
      this.raycaster.far = BOOM_LENGTH_M;
      for (const [rightAmt, upAmt] of COLLISION_PROBE_OFFSETS) {
        this.probeOrigin.copy(this.shoulderAnchor).addScaledVector(this.rightVec, rightAmt);
        this.probeOrigin.y += upAmt;
        this.probeDirection.copy(this.forwardVec).negate();
        this.raycaster.set(this.probeOrigin, this.probeDirection);
        const hit = this.raycaster.intersectObject(this.layoutGroup, true)[0];
        if (hit) targetBoomLength = Math.min(targetBoomLength, Math.max(MIN_CAMERA_DISTANCE, hit.distance - CAMERA_COLLISION_MARGIN));
      }
    }
    // Asymmetric, and deliberately so (2026-08-25). Pulling IN is the safety
    // direction — it is what keeps the eye out of geometry — so it applies
    // immediately; only easing back OUT once the wall clears is smoothed.
    //
    // A single symmetric blend cannot be right here, and measurement showed why:
    // with MIN_CAMERA_DISTANCE lowered so wall hits always win, the overridden
    // frames went to zero, but the eye was still past a wall surface on 5 of 238
    // frames walking forward, lagging up to 2.49m behind its own target. The
    // blend was smoothing the one direction where lag means clipping through
    // the wall.
    //
    // This does reinstate a fast pull-in, which is exactly what the 2026-08-21
    // blend was added to remove (a ~0.7m single-frame eye jump from the probe
    // ring flickering between "clear" and "grazing a corner"). That trade is
    // taken knowingly: a quick pull-in is a camera move, being inside a wall is
    // rendering garbage, and the flicker's *recovery* is still smoothed, so one
    // grazing frame gives a single tug and a soft return rather than a
    // sustained oscillation.
    this.currentBoomLength = targetBoomLength < this.currentBoomLength
      ? targetBoomLength
      : expApproach(this.currentBoomLength, targetBoomLength, dtSeconds, BOOM_BLEND_TAU_S);
    const { eye, lookAt } = computeShoulderCameraPose({
      playerX: pos.x, playerZ: pos.z, viewYaw, viewPitch, shoulderSide,
      shoulderOffsetM: SHOULDER_OFFSET_M, shoulderHeightM: SHOULDER_HEIGHT_M, boomLengthM: this.currentBoomLength,
    });
    this.camera.position.set(eye.x, eye.y, eye.z);
    this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);

    if (this.shakeElapsedMs < this.shakeDurationMs) {
      this.shakeElapsedMs += dtSeconds * 1000;
      const decay = 1 - Math.min(1, this.shakeElapsedMs / this.shakeDurationMs);
      const mag = this.shakeAmplitudeM * decay;
      this.camera.position.x += (Math.random() * 2 - 1) * mag;
      this.camera.position.y += (Math.random() * 2 - 1) * mag;
      this.camera.position.z += (Math.random() * 2 - 1) * mag;
      this.camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
      if (this.shakeElapsedMs >= this.shakeDurationMs) this.shakeAmplitudeM = 0;
    }

    const targetFov = this.fovTargetDeg(pos);
    this.currentFovDeg = expApproach(this.currentFovDeg, targetFov, dtSeconds, FOV_BLEND_TAU_S);

    this.currentFovKickDeg = stepFovKick(this.currentFovKickDeg, sprintOrDashActive, dtSeconds, {
      kickDeg: FOV_SPRINT_KICK_DEG, attackTauS: FOV_KICK_ATTACK_TAU_S, releaseTauS: FOV_KICK_RELEASE_TAU_S, reducedMotion: this.reducedMotion,
    });

    this.camera.fov = this.currentFovDeg + this.currentFovKickDeg;
    this.camera.updateProjectionMatrix();
  }

  /**
   * "Corridors feel tight, halls feel open" — averages a horizontal ring of
   * raycasts from the player's own position (not the camera, so it reflects
   * the space itself rather than the current view angle) and maps the
   * average opening distance onto the spec's 55°-70° FOV range.
   */
  private fovTargetDeg(pos: { x: number; z: number }): number {
    if (!this.layoutGroup) return FOV_OPEN_ROOM_DEG;
    this.raycaster.near = 0.05;
    this.raycaster.far = OPENNESS_PROBE_MAX_M;
    let total = 0;
    for (const angle of OPENNESS_PROBE_ANGLES) {
      this.probeOrigin.set(pos.x, SHOULDER_HEIGHT_M, pos.z);
      this.probeDirection.set(Math.sin(angle), 0, Math.cos(angle));
      this.raycaster.set(this.probeOrigin, this.probeDirection);
      const hit = this.raycaster.intersectObject(this.layoutGroup, true)[0];
      total += hit ? hit.distance : OPENNESS_PROBE_MAX_M;
    }
    const avgOpenness = total / OPENNESS_PROBE_ANGLES.length;
    const t = Math.min(1, Math.max(0, (avgOpenness - OPENNESS_CORRIDOR_M) / (OPENNESS_OPEN_ROOM_M - OPENNESS_CORRIDOR_M)));
    return FOV_CORRIDOR_DEG + t * (FOV_OPEN_ROOM_DEG - FOV_CORRIDOR_DEG);
  }

  // ─── World-state beacons ────────────────────────────────────────────

  addDoorBeacon(id: string, x: number, z: number): void {
    this.addBeacon(this.doorBeacons, id, x, z, 2.2, 'UNBARRED');
  }
  updateDoorState(id: string, state: 'UNBARRED' | 'BARRED'): void {
    this.updateBeacon(this.doorBeacons, id, state, state === 'UNBARRED');
  }
  removeDoorBeacon(id: string): void {
    this.removeBeacon(this.doorBeacons, id);
  }

  addPortcullisBeacon(id: string, x: number, z: number): void {
    this.addBeacon(this.portcullisBeacons, id, x, z, 3.0, 'SEALED');
  }
  updatePortcullisState(id: string, state: 'SEALED' | 'CRANKING' | 'OPEN'): void {
    this.updateBeacon(this.portcullisBeacons, id, state, state === 'OPEN');
  }
  removePortcullisBeacon(id: string): void {
    this.removeBeacon(this.portcullisBeacons, id);
  }

  addChestBeacon(id: string, x: number, z: number): void {
    this.addBeacon(this.chestBeacons, id, x, z, 1.0, 'CLOSED', 1.2);
  }
  updateChestState(id: string, state: 'CLOSED' | 'OPEN'): void {
    // Chests are always the interaction target regardless of state (one-way, not blocking) — never hidden, just recolored.
    this.updateBeacon(this.chestBeacons, id, state === 'OPEN' ? 'CHEST_OPEN' : 'CLOSED', false);
  }
  removeChestBeacon(id: string): void {
    this.removeBeacon(this.chestBeacons, id);
  }

  private addBeacon(map: Map<string, THREE.Mesh>, id: string, x: number, z: number, height: number, initialState: string, yOffset = 0): void {
    const geometry = new THREE.CylinderGeometry(0.22, 0.22, height, 12);
    const material = new THREE.MeshStandardMaterial({
      color: BEACON_COLORS[initialState] ?? 0xffffff, emissive: 0x221100, transparent: true, opacity: 0.85,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(x, yOffset + height / 2, z);
    this.scene.add(mesh);
    map.set(id, mesh);
  }

  private updateBeacon(map: Map<string, THREE.Mesh>, id: string, state: string, hidden: boolean): void {
    const mesh = map.get(id);
    if (!mesh) return;
    (mesh.material as THREE.MeshStandardMaterial).color.setHex(BEACON_COLORS[state] ?? 0xffffff);
    mesh.visible = !hidden;
  }

  private removeBeacon(map: Map<string, THREE.Mesh>, id: string): void {
    const mesh = map.get(id);
    if (!mesh) return;
    this.scene.remove(mesh);
    map.delete(id);
  }

  // ─── Braziers — a real light, not a beacon: douse/light is legible as illumination, not color ───

  addBrazierVisual(id: string, x: number, z: number): void {
    this.ensureLightPools();
    const f = flamePosition(x, z, this.lightAnchors);
    this.braziers.set(id, { x, z, pos: new THREE.Vector3(f.x, f.y, f.z), lit: true });
    this.lastSlotAssignAt = -Infinity;
  }
  updateBrazierState(id: string, state: 'LIT' | 'DOUSED'): void {
    const b = this.braziers.get(id);
    if (b) b.lit = state === 'LIT';
    this.lastSlotAssignAt = -Infinity; // the lit set changed: refresh probes and slots next frame
  }
  removeBrazierVisual(id: string): void {
    this.braziers.delete(id);
    this.lastSlotAssignAt = -Infinity;
  }

  /** Who carries a lit lantern. `root` null puts it out (or forgets a body that left view). */
  setLanternHolder(id: string, root: THREE.Object3D | null): void {
    if (root) { this.ensureLightPools(); this.lanternHolders.set(id, root); } else this.lanternHolders.delete(id);
  }

  /**
   * Whether `pos` is lit by the server's rule (`isLit`, minus line of sight) —
   * what decides a monster's tells, see `monster-reveal.ts`. Reads probe lists
   * rebuilt with the slot assignment, so it allocates nothing per call.
   */
  isLitAt(pos: { x: number; z: number }): boolean {
    return isLitAt(pos, this.litBrazierProbes, this.lanternProbes);
  }
  private litBrazierProbes: { x: number; z: number }[] = [];
  private lanternProbes: THREE.Vector3[] = [];

  /**
   * Hands the pooled lights to the nearest sources and eases intensities.
   * Slot assignment runs at 5Hz — the nearest torches do not change faster
   * than a body walks — while positions and fades are per frame.
   */
  private stepLights(): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastLightStepAt) / 1000);
    this.lastLightStepAt = now;
    const focus = this.camera.position;

    if (now - this.lastSlotAssignAt >= 200) {
      this.lastSlotAssignAt = now;
      this.litBrazierProbes = [];
      for (const b of this.braziers.values()) if (b.lit) this.litBrazierProbes.push(b);
      this.lanternProbes = [...this.lanternHolders.values()].map((root) => root.position);
      if (this.brazierSlots.length === 0) return;
      const lit: LightSourcePos[] = [];
      for (const [id, b] of this.braziers) if (b.lit) lit.push({ id, x: b.pos.x, z: b.pos.z });
      const next = assignLightSlots(this.brazierSlots.map((s) => s.id), lit, focus);
      next.forEach((id, i) => {
        const slot = this.brazierSlots[i];
        if (slot.id !== id) { slot.id = id; slot.level = 0; }
      });
      const holders: LightSourcePos[] = [];
      for (const [id, root] of this.lanternHolders) holders.push({ id, x: root.position.x, z: root.position.z });
      const lanterns = assignLightSlots(this.lanternSlots.map((s) => s.id), holders, focus);
      lanterns.forEach((id, i) => { this.lanternSlots[i].id = id; });
    }

    const step = dt / LIGHT_SLOT_FADE_S;
    for (const slot of this.brazierSlots) {
      const b = slot.id ? this.braziers.get(slot.id) : undefined;
      const target = b?.lit ? 1 : 0;
      slot.level = target > slot.level ? Math.min(target, slot.level + step) : Math.max(target, slot.level - step);
      if (b) slot.light.position.copy(b.pos);
      slot.light.intensity = BRAZIER_INTENSITY * slot.level;
    }
    for (const slot of this.lanternSlots) {
      const root = slot.id ? this.lanternHolders.get(slot.id) : undefined;
      if (root) slot.light.position.set(root.position.x, root.position.y + LANTERN_HEIGHT_M, root.position.z);
      slot.light.intensity = root ? LANTERN_INTENSITY : 0;
    }
  }

  render(): void {
    this.stepLights();
    this.postProcessing.render();
    const ratio = this.adaptiveResolution.sample(performance.now());
    if (ratio !== null) {
      this.renderer.setPixelRatio(ratio);
      this.postProcessing.setPixelRatio(ratio);
    }
  }

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.postProcessing.setSize(window.innerWidth, window.innerHeight);
  }

  dispose(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.postProcessing.dispose();
    this.renderer.dispose();
  }
}
